import { BANNER_CDN } from "../PropertyBanner/PropertyBanner";
import type { PropertyParams } from "src/shared/models/containers/account.model";

/**
 * Hardcoded promo banners shown on every property automatically.
 *
 * KEEP IN SYNC with resident-portal-api/src/helpers/bannerCampaigns.ts. The API
 * owns the canonical list (and validates suppression keys against it); this
 * mirror exists so the resident app can render campaigns from the property
 * payload it already fetches, without an extra round trip. Adding a campaign
 * means editing both files.
 *
 * Replacing artwork: the CloudFront distribution EXCLUDES query strings from
 * its cache key, so ?v= / ?t= do not bust it and re-uploading over the same key
 * leaves residents on the cached image. Ship new artwork by bumping the version
 * suffix in imageKey (…-v2.png -> …-v3.png) here AND in the API helper.
 */
export interface BannerCampaign {
  key: string;
  label: string;
  imageKey: string;
  link: string | null;
  audience: BannerAudience;
  sortOrder: number;
}

export type BannerAudience = "all" | "credbuild_unenrolled";

export const BANNER_CAMPAIGNS: readonly BannerCampaign[] = [
  {
    key: "credbuild",
    label: "Report your rent and build credit with CredBuild",
    imageKey: "banners/campaigns/credbuild-home-v2.png",
    link: "/rent/reporting/home",
    audience: "credbuild_unenrolled",
    sortOrder: 100,
  },
];

/** One rotating banner. `sources` is a fallback chain: first image that loads wins. */
export interface BannerSlide {
  key: string;
  sources: string[];
  link?: string | null;
  alt: string;
}

export interface BannerAudienceContext {
  /** User.isCreditReportingEnabled */
  isEnrolledInCredBuild: boolean;
  /** Moved in on an active lease. Pre-move-in residents cannot enrol yet. */
  isActiveResident: boolean;
}

/**
 * Whether a resident falls inside an audience.
 *
 * "credbuild_unenrolled" is the CredBuild enrolment promo: hidden from anyone
 * already enrolled, and from pre-move-in residents who are not yet able to
 * enrol. "all" is unconditional.
 */
export const audienceAllows = (
  audience: BannerAudience | undefined,
  ctx: BannerAudienceContext
): boolean =>
  audience !== "credbuild_unenrolled" ||
  (ctx.isActiveResident && !ctx.isEnrolledInCredBuild);

const cacheBust = (url: string, ts?: string | null) =>
  ts ? `${url}?t=${new Date(ts).getTime()}` : url;

/**
 * Build the ordered home-banner rotation for a resident.
 *
 * The property's own uploaded banner comes first, then every campaign the
 * property has not suppressed. Each entry is filtered by its own audience, so a
 * property banner and a campaign banner gate independently.
 *
 * Slides whose images 404 are dropped by the carousel at render time, which is
 * how a property with no uploaded banner ends up showing campaigns only.
 */
export const resolveHomeBannerSlides = (
  property: PropertyParams | undefined | null,
  ctx: BannerAudienceContext
): BannerSlide[] => {
  if (!property?.abbreviation) return [];

  const slides: BannerSlide[] = [];
  const { abbreviation } = property;

  // homeBannerUpdatedAt is the source of truth for "a home banner exists".
  // We cannot infer it from whether the image loads: the CDN in front of the
  // bucket keeps serving a deleted object from cache for hours, so a banner
  // staff deleted would keep rendering. The column is nulled on delete, so
  // trusting it makes delete take effect immediately.
  if (
    property.homeBannerUpdatedAt &&
    audienceAllows(property.homeBannerAudience, ctx)
  ) {
    slides.push({
      key: "property",
      // ONLY the -home key. The landing banner is a 2:3 portrait sign-in hero;
      // cropping it into this 4:1 strip shows a thin slice of a portrait image
      // and, worse, consumes a rotation slot that a campaign designed for the
      // space could use. A property with no home banner contributes no slide.
      sources: [
        cacheBust(
          `${BANNER_CDN}/banners/${abbreviation}-home.png`,
          property.homeBannerUpdatedAt
        ),
      ],
      link: property.homeBannerLink,
      alt: `${abbreviation} banner`,
    });
  }

  const suppressed = new Set(property.suppressedBannerCampaigns ?? []);
  BANNER_CAMPAIGNS.filter(
    (c) => !suppressed.has(c.key) && audienceAllows(c.audience, ctx)
  )
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .forEach((c) => {
      slides.push({
        key: `campaign:${c.key}`,
        sources: [`${BANNER_CDN}/${c.imageKey}`],
        link: c.link,
        alt: c.label,
      });
    });

  return slides;
};
